








































import { PropType, computed, defineComponent, ref, getCurrentInstance } from '@vue/composition-api';
import { Message, RecommendData, RecommendValue } from '../../dataType';
import Card from './Card.vue';
import { useLocale } from '../../hooks/useLocale';
import { useRecommendMessage, useMessage } from '../../hooks/useMessage';
import { useConfig } from '../../hooks/useConfig';
import { useMessageInteract } from '../../hooks/useMessageInteract';

type FromData = Record<string, string>;

export default defineComponent({
  components: {
    Card
  },
  props: {
    message: {
      type: Object as PropType<Message<RecommendData, FromData>>,
      required: true
    }
  },
  setup(props) {
    const { t } = useLocale();
    const instance = getCurrentInstance()?.proxy!;
    const { isSingleRecommend } = useRecommendMessage(props.message);
    const { addUserTextMessage, updateMessage, robotWaitWithAsyncOperation, sendMessageAndUpdateId } = useMessage();
    const { isDisabled } = useMessageInteract(props.message);
    const loading = ref<boolean>(false);
    const filterList = computed<{
      fieldKey: string;
      label: string;
      options: {
        label: string;
        value: string;
      }[];
    }[]>(() => {
      return props.message.content.filterOptions?.map(filter => {
        const fieldKey = filter.code;
        const options = filter.options.map(item => {
          return {
            label: item.name,
            value: item.id.join('-')
          };
        });
        return {
          fieldKey,
          label: filter.selectText || `请选择${t(`issue.field.${fieldKey}`)}`,
          options
        };
      }) || [];
    });

    const fromData = computed<FromData>(() => {
      const getKeepValue = (key: string) => {
        return props.message.keepData?.[key];
      };
      return filterList.value.reduce((acc, cur) => {
        // 默认选择第一个
        acc[cur.fieldKey] = getKeepValue(cur.fieldKey) || cur.options[0].value;
        return acc;
      }, {} as FromData);
    });

    const changeValue = (key: string, value: string) => {
      updateMessage(props.message.id, {
        ...props.message,
        keepData: {
          ...props.message.keepData,
          [key]: value
        }
      });
    };

    const confirm = async (event: Event) => {
      if (loading.value) {
        return;
      }
      let sendTextInput = props.message.content.base.placeholderContent || '';
      // 把表单的值转换成后端需要的格式
      const p = Object.keys(fromData.value).reduce((acc: Record<string, RecommendValue | null>, key: string) => {
        // 获取到选择的key的选项
        const find = props.message.content.filterOptions?.find(item => item.code === key);
        if (!find) {
          acc[key] = null;
        } else {
          // 通过value找到对应的选项值
          const option = find.options.find((option: RecommendValue) => {
            const value = option.id.join('-');
            return value === fromData.value[key];
          });
          sendTextInput = sendTextInput.replace(`{{${key}.name}}`, `「${option!.name}」`);
          acc[key] = option || null;
        }
        return acc;
      }, {});
      loading.value = true;
      try {
        const msg = addUserTextMessage(sendTextInput);
        const params = {
          id: props.message.content.base.id || 0,
          inputText: {
            text: sendTextInput,
            push: true,
            save: true
          },
          actionType: 'query_data',
          actionJson: {
            chatId: props.message.content.base.chatId,
            intentCode: props.message.content.intent,
            params: {
              ...props.message.content.params,
              ...p
            },
            gptResponse: props.message.content.gptResponse,
            placeholderContent: props.message.content.base.placeholderContent,
            source: props.message.content.base.source,
            taskId: props.message.content.base.taskId
          }
        };
        await robotWaitWithAsyncOperation(() => sendMessageAndUpdateId(msg, 'confirm', params, {
          intent: props.message.content.intent,
          event,
          parabolaAnimation: true
        }));
        if (props.message.content.recommendType === 'continue') {
          instance.$track.send('WebIM_SendIntent', {
            Send_Type: '追问推荐',
            Intent_Type: props.message.content.intent
          }, true);
        }
      } finally {
        loading.value = false;
      }
    };
    return {
      t,
      isSingleRecommend,
      filterList,
      fromData,
      loading,
      isDisabled,
      changeValue,
      confirm
    };
  }
});
